import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell from "../../components/layout/AuthShell";
import GoogleButton from "../../components/common/GoogleButton";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import api from "../../lib/api";
import { formatApiError } from "../../context/AuthContext";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [ageConfirm, setAgeConfirm] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const fe = {};
    if (!form.first_name.trim()) fe.first_name = "First Name is required.";
    if (!form.last_name.trim()) fe.last_name = "Last Name is required.";
    setFieldErrors(fe);
    if (Object.keys(fe).length) return;
    if (!ageConfirm || !acceptTerms) {
      setError("You must confirm you are 18+ and accept the Terms & Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", {
        first_name: form.first_name.trim(), last_name: form.last_name.trim(),
        email: form.email, password: form.password, age_confirm: ageConfirm, accept_terms: acceptTerms,
        marketing_consent: marketingConsent,
      });
      toast.success("Account created — check your email for the verification code.");
      navigate("/verify-otp", { state: { email: form.email } });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create Account"
      subtitle="Reserve fireworks online and collect in-store."
      footer={
        <p>
          Already have an account?{" "}
          <Link to="/login" className="text-gold hover:underline" data-testid="link-login">Sign in</Link>
        </p>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="first_name" className="text-white/80">First name</Label>
            <Input id="first_name" value={form.first_name} onChange={upd("first_name")} placeholder="Jane" data-testid="register-first-name-input" className="bg-navy border-white/15 text-white h-11 focus-visible:ring-gold" />
            {fieldErrors.first_name && <p className="text-xs text-firered" data-testid="register-first-name-error">{fieldErrors.first_name}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="last_name" className="text-white/80">Last name</Label>
            <Input id="last_name" value={form.last_name} onChange={upd("last_name")} placeholder="Doe" data-testid="register-last-name-input" className="bg-navy border-white/15 text-white h-11 focus-visible:ring-gold" />
            {fieldErrors.last_name && <p className="text-xs text-firered" data-testid="register-last-name-error">{fieldErrors.last_name}</p>}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-white/80">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={upd("email")} placeholder="you@example.com" data-testid="register-email-input" className="bg-navy border-white/15 text-white h-11 focus-visible:ring-gold" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-white/80">Password</Label>
          <Input id="password" type="password" required minLength={8} value={form.password} onChange={upd("password")} placeholder="At least 8 characters" data-testid="register-password-input" className="bg-navy border-white/15 text-white h-11 focus-visible:ring-gold" />
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={ageConfirm} onCheckedChange={setAgeConfirm} data-testid="register-age-checkbox" className="mt-0.5 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:text-ink" />
          <span className="text-sm text-white/70">I confirm I am <span className="text-gold font-semibold">18 years or older</span>. Challenge 25 applies on collection.</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={acceptTerms} onCheckedChange={setAcceptTerms} data-testid="register-terms-checkbox" className="mt-0.5 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:text-ink" />
          <span className="text-sm text-white/70">I accept the Terms & Conditions and Privacy Policy.</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={marketingConsent} onCheckedChange={setMarketingConsent} data-testid="register-marketing-checkbox" className="mt-0.5 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:text-ink" />
          <span className="text-sm text-white/70">I would like to receive news, seasonal updates and special offers from Clacton Fireworks. <span className="text-white/40">(optional)</span></span>
        </label>

        {error && <p className="text-sm text-firered" data-testid="register-error">{error}</p>}

        <Button type="submit" disabled={loading} className="w-full cf-gold-btn font-semibold h-11" data-testid="register-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <GoogleButton label="Sign up with Google" marketingConsent={marketingConsent} />
    </AuthShell>
  );
}
